import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { MessageCircle, Users } from "lucide-react";

const daysAgo = (n) => (n === 0 ? "today" : n === 1 ? "yesterday" : `${n} days ago`);

export const YogaMembers = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/yoga/admin/members").then(({ data: res }) => setData(res)).catch(() => {});
  }, []);

  if (!data) return null;

  const whatsapp = (m) => {
    const num = (m.phone || "").replace(/\D/g, "");
    const msg =
      `Namaste ${m.name}! You are on day ${m.current_day} of the 21-day yoga journey. ` +
      `Shall we roll out the mat today? Your next practice is waiting.`;
    return num ? `https://wa.me/${num}?text=${encodeURIComponent(msg)}` : "";
  };

  return (
    <div className="mt-12" data-testid="yoga-members">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h3 className="font-display text-2xl flex items-center gap-2"><Users size={18}/> Members</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Who is on the 21-day programme, the day they reached, and who has gone quiet for 4 days or more.
          </p>
        </div>
        <div className="flex gap-2">
          <Badge variant="outline" className="border-primary/40 text-primary bg-primary/10" data-testid="members-active">
            {data.active} active
          </Badge>
          <Badge variant="outline" className="border-secondary/50 text-secondary bg-secondary/10" data-testid="members-quiet">
            {data.quiet} gone quiet
          </Badge>
        </div>
      </div>

      {data.members.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="members-empty">
          No one has joined the programme yet.
        </p>
      ) : (
        <div className="space-y-3">
          {data.members.map((m) => (
            <Card key={m.user_id} className="border-border" data-testid={`member-row-${m.user_id}`}>
              <CardContent className="p-5 flex flex-wrap items-center justify-between gap-4">
                <div className="min-w-0">
                  <div className="font-medium flex items-center gap-2">
                    {m.name}
                    {m.went_quiet && (
                      <Badge variant="outline" className="text-[10px] border-secondary/50 text-secondary" data-testid={`member-quiet-${m.user_id}`}>
                        Gone quiet
                      </Badge>
                    )}
                    {!m.active && (
                      <Badge variant="outline" className="text-[10px] text-muted-foreground">Expired</Badge>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">{m.email}</div>
                </div>
                <div className="text-sm">
                  Day <span className="font-display text-2xl">{m.current_day}</span>
                  <span className="text-muted-foreground text-xs"> · {m.days_completed} completed</span>
                </div>
                <div className="text-xs text-muted-foreground">
                  {m.last_activity
                    ? `Last practice ${m.quiet_days !== null ? daysAgo(m.quiet_days) : new Date(m.last_activity).toLocaleDateString()}`
                    : "Has not started"}
                  {m.expires_at && <div>Access until {new Date(m.expires_at).toLocaleDateString()}</div>}
                </div>
                {whatsapp(m) && (
                  <a
                    href={whatsapp(m)}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs text-[#128C7E] hover:underline flex items-center gap-1"
                    data-testid={`member-nudge-${m.user_id}`}
                  >
                    <MessageCircle size={13}/> Nudge on WhatsApp
                  </a>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default YogaMembers;
